import React, { useState, useEffect, useMemo } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { Info, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Card, GradientText, Button } from '../../components/ui/index.tsx';
import { buildApplicantVoice, formatCurrency } from '../../utils/index.ts';
import { GOAL_INSIGHTS, ACAT_LEVEL_DATA, FAMILY_ESTATE_INSIGHTS } from '../../constants/index.ts';
import FinancialSnapshot from '../../components/FinancialSnapshot.tsx';
import MobileInsightSheet from '../../components/MobileInsightSheet.tsx';
import MentalCapacityProcessModal from '../../components/modals/MentalCapacityProcessModal.tsx';
import { WelcomeInsight, AcatInsightCard } from './ClientProfileInsights.tsx';
import {
    Step1_WhoIsCompleting,
    Step4_LivingSituation,
    Step2_YourDetails,
    Step3_GoalsCareNeeds,
    Step4_FamilyHome,
    Step4_AltLiving,
    Step5_Assets,
    Step6_Liabilities,
    Step7_IncomeExpenses,
    Step8_FamilyEstate,
} from './ClientProfileSteps.tsx';
import Step4LivingInsightPanel from './Step4LivingInsightPanel.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindGoals, FactFindFamily } from '../../../../shared/src/calculation/factFindTypes.ts';

export interface ClientProfileProps {
    setActivePage: (page: string) => void;
    factFind: DraftFactFind;
    setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    strategyOptions?: Record<string, unknown>;
    clientProfileStep: number;
    setClientProfileStep: (step: number) => void;
    hideApplicantContactFields?: boolean;
    showStructureAdviserFields?: boolean;
}

/** What the mobile sheet's bar calls each step's supporting content (see MobileInsightSheet).
 *  Steps with no insight column — 1 and 2 — are absent, and no bar is shown for them. */
const MOBILE_INSIGHT_TITLES: Record<number, string> = {
    3: 'About your goals',
    4: 'Your living arrangements',
    5: 'Financial Snapshot',
    6: 'Financial Snapshot',
    7: 'Financial Snapshot',
    8: 'Family & estate',
};

/** True while the viewport is at Tailwind's `xl` or wider — the width at which the insight
 *  column is laid out beside the form. Below it the same content goes in the bottom sheet.
 *
 *  `xl` (1280) rather than `lg` (1024): an iPad Pro in portrait is exactly 1024, so it tipped
 *  into the three-column layout with the persistent 256px nav also on screen, leaving the
 *  snapshot about 250px to render in — the composition legend truncated to "C.", "S.", "Ca."
 *  and the totals wrapped (seen 2026-08-19). A tablet in portrait is better served by the
 *  bottom sheet, which gets the full width; landscape (1366) still gets the column.
 *  Driven in JS rather than CSS so the content is mounted ONCE: rendering both and hiding one
 *  would run the Financial Snapshot's calculation twice on every edit. */
const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches,
    );
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia('(min-width: 1280px)');
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mq.addEventListener('change', onChange);
        setIsDesktop(mq.matches);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
};

const buildProfileStepLabels = (possessive: string) => [
    "Who's Completing This Form",
    `${possessive} Details`,
    'Goals',
    'Living Arrangements',
    'Assets',
    'Liabilities',
    'Income/Expenses',
    'Family/Estate',
];

const ClientProfile = ({
    setActivePage,
    factFind,
    setFactFind,
    clientProfileStep,
    setClientProfileStep,
    hideApplicantContactFields = true,
    showStructureAdviserFields = false,
}: ClientProfileProps) => {
    const [step, setStep] = useState(clientProfileStep);

    useEffect(() => {
        setStep(clientProfileStep);
    }, [clientProfileStep]);

    // Back to the top of the form on every step change — see scrollAppToTop for why this cannot be
    // a querySelector on '.overflow-y-auto' (it matched the sidebar, so this never moved the page).
    useEffect(() => {
        scrollAppToTop();
    }, [step]);

    const [showAgePensionContext, setShowAgePensionContext] = useState(false);
    const totalSteps = 8;

    const nextStep = () => {
        const newStep = Math.min(step + 1, totalSteps);
        setStep(newStep);
        setClientProfileStep(newStep);
        // The effect on `step` above does the scrolling; this second pass catches the case where the
        // new step renders taller than the last and the container grows after the first attempt.
        setTimeout(() => scrollAppToTop(), 100);
    };

    const prevStep = () => {
        const newStep = Math.max(step - 1, 1);
        setStep(newStep);
        setClientProfileStep(newStep);
        // The effect on `step` above does the scrolling; this second pass catches the case where the
        // new step renders taller than the last and the container grows after the first attempt.
        setTimeout(() => scrollAppToTop(), 100);
    };

    const updateFactFind = (section: string, field: string, value: unknown) => {
        setFactFind(prev => ({
            ...prev,
            [section]: { ...(prev[section as keyof DraftFactFind] as Record<string, unknown> | undefined), [field]: value }
        }));
    };

    const setMainData = (field: string, value: string | number) => {
        setFactFind((prev) => {
            if (field !== 'adviceFor') {
                return { ...prev, [field]: value };
            }
            const next: DraftFactFind = { ...prev, adviceFor: value as DraftFactFind['adviceFor'] };
            if (value === 'couple') {
                const ownership = prev.home?.ownership;
                if (!ownership || ownership === 'client') {
                    next.home = { ...prev.home, ownership: 'joint' };
                }
            }
            return next;
        });
    };

    const isPartnered = factFind.adviceFor === 'couple';
    /**
     * Client OR partner, matching the engine's own `occupantHasOwnHomeResidency`
     * (shared/src/calculation/entityHomeRules.ts). It tested the client alone, so a couple where
     * the client had already moved into residential care and the spouse still lived in the family
     * home fell to the alternative-living branch — whose four cases all evaluated false, leaving
     * step 4 as a bare heading and the home value never asked for, while the engine went on
     * treating them as home-owning occupants.
     */
    const ownsHome =
        factFind.client?.currentResidency === 'own_home' ||
        (isPartnered && factFind.partner?.currentResidency === 'own_home');

    /**
     * A couple can live apart — one already in residential care while the other is at home, one in
     * a village while the other rents. Step 4 used to pick ONE set of questions for the household:
     * if either of them owned the home, everybody got the family-home questions and nothing else,
     * so a village entry contribution was never asked for at all (adviser-reported, 2026-08-20).
     *
     * The home is shared, so it is asked once; every other arrangement belongs to a person, so the
     * two sections now appear together whenever both apply.
     *
     * `aged_care` is in the list even though it asks NOTHING. It was left out at first on the
     * reasoning that somebody already in residential care has no arrangement to describe — true of
     * the questions, but it meant the step named Living Arrangements said nothing whatsoever about
     * one of the two people living in it (adviser-reported, 2026-08-20: a couple with one already
     * in care showed only the family home). The section states what that answer already decides
     * instead.
     */
    const ALTERNATIVE_RESIDENCIES = ['renting', 'with_family', 'retirement_village', 'granny_flat', 'aged_care'];
    const hasAlternativeArrangement = [factFind.client?.currentResidency, isPartnered ? factFind.partner?.currentResidency : undefined]
        .some((residency) => ALTERNATIVE_RESIDENCIES.includes(String(residency)));

    /**
     * Nothing below the chooser renders until the question is answered for everyone it applies to.
     * The default used to be 'own_home' for both people, so a profile modelled a home owner whether
     * or not anyone had said so; it is blank now, and this is what keeps a blank from simply
     * flowing through. It also has to gate the Next button: most of the engine reads a blank as
     * 'own_home', but entityHomeRules does not, so an unanswered profile would assess an
     * entity-held home differently depending on which rule looked at it.
     */
    const residencyAnswered =
        !!factFind.client?.currentResidency && (!isPartnered || !!factFind.partner?.currentResidency);
    // Whether labels say "your home" or "Bob & Sally's home" — see utils/applicantVoice.ts. Built
    // once here and passed down, so every step (and the step list above the form) speaks the same way.
    const voice = useMemo(() => buildApplicantVoice(factFind), [factFind]);

    const [showMentalCapacityModal, setShowMentalCapacityModal] = useState(false);
    const isDesktop = useIsDesktop();
    // The net worth the Financial Snapshot is DISPLAYING, reported up by the card itself so the
    // collapsed mobile bar shows that same figure rather than a second, independently computed one.
    const [snapshotNetWorth, setSnapshotNetWorth] = useState<number | null>(null);

    const getContextualInsight = () => {
        switch (step) {
            case 1:
                return <WelcomeInsight title="Why We Ask This First" text="Your answer sets how the rest of the questions are worded — whether we ask about your own circumstances or about the person you're helping — so nothing later is ambiguous about whose home, income or care we mean." icon={Info} />;
            case 2:
                return <WelcomeInsight title="Let's Get Started" text={`The first step to clarity is understanding ${voice.possessive} current situation. Answer a few questions to begin building ${voice.pronounPossessive} personalised plan.`} icon={Info} />;
            case 3: {
                const { goals, careNeeds } = factFind;
                const isPartnered = factFind.adviceFor === 'couple';
                const clientAcat = careNeeds?.clientAcat;
                const partnerAcat = careNeeds?.partnerAcat;
                const insightsToShow: React.ReactNode[] = [];
                const goalKeys: (keyof FactFindGoals)[] = ['home_care', 'aged_care', 'retirement_village', 'granny_flat', 'downsize_home'];
                goalKeys.forEach(key => {
                    if (goals?.[key]) {
                        insightsToShow.push(<WelcomeInsight key={key} {...GOAL_INSIGHTS[key]} />);
                    }
                });
                const needsAcat = (clientAcat?.status === 'no') || (isPartnered && partnerAcat?.status === 'no');
                if (goals?.home_care && needsAcat) {
                    insightsToShow.push(<WelcomeInsight key="acat_assessment" {...GOAL_INSIGHTS.acat_assessment} />);
                }
                const clientLevel = clientAcat?.status === 'yes' ? ACAT_LEVEL_DATA[clientAcat.homeCareLevel || ''] : null;
                const partnerLevel = isPartnered && partnerAcat?.status === 'yes' ? ACAT_LEVEL_DATA[partnerAcat.homeCareLevel || ''] : null;
                if (clientLevel) {
                    insightsToShow.push(<AcatInsightCard key="client_acat_result" personName={factFind.client?.firstName || 'Applicant 1'} levelData={clientLevel} />);
                }
                if (partnerLevel) {
                    insightsToShow.push(<AcatInsightCard key="partner_acat_result" personName={factFind.partner?.firstName || 'Partner'} levelData={partnerLevel} />);
                }
                if (insightsToShow.length === 0) {
                    insightsToShow.push(<WelcomeInsight key="default" {...GOAL_INSIGHTS.default} />);
                }
                return <div className="space-y-4">{insightsToShow}</div>;
            }
            case 4:
                // Nothing to explain until they have said where they live.
                return residencyAnswered ? <Step4LivingInsightPanel factFind={factFind} ownsHome={ownsHome} /> : null;
            case 5:
            case 6:
            case 7:
                return <FinancialSnapshot factFind={factFind} onNetWorthChange={setSnapshotNetWorth} />;
            case 8: {
                const family = factFind.family || {};
                const isPartnered = factFind.adviceFor === 'couple';
                const defaultInsight = FAMILY_ESTATE_INSIGHTS.find(i => i.key === 'default');
                const additionalInsights = FAMILY_ESTATE_INSIGHTS.filter(
                    (insight) => insight.key !== 'default' && insight.trigger(family as FactFindFamily, factFind.completer, isPartnered)
                );
                if (!defaultInsight) return null;
                return (
                    <>
                        <div className="space-y-4">
                            <WelcomeInsight
                                title={defaultInsight.title}
                                text={defaultInsight.text}
                                icon={defaultInsight.icon}
                            />
                            {additionalInsights.map(insight =>
                                <WelcomeInsight
                                    key={insight.key}
                                    title={insight.title}
                                    text={insight.text}
                                    icon={insight.icon}
                                    onLearnMore={insight.hasModal ? () => setShowMentalCapacityModal(true) : undefined}
                                />
                            )}
                        </div>
                    </>
                );
            }
            default:
                return null;
        }
    };

    const contextualInsight = getContextualInsight();
    const mobileInsightTitle = MOBILE_INSIGHT_TITLES[step];

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8">
                <button onClick={() => setActivePage('Dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-4">
                    <ChevronLeft size={20} /> Back to Dashboard
                </button>
                <h1 className="text-4xl font-bold text-gray-900"><GradientText>Client Profile</GradientText></h1>
                <p className="text-lg text-slate-600 mt-1">Let's gather some information to help us understand {voice.possessive} situation.</p>
                <p className="mt-3 text-sm font-semibold text-[#001E7A]">
                    Step {step} of {totalSteps}: {buildProfileStepLabels(voice.Possessive)[step - 1]}
                </p>
            </div>

            <div className="w-full bg-slate-200 rounded-full h-2.5 mb-8" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={totalSteps} aria-label={`Profile progress: step ${step} of ${totalSteps}`}>
                <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }}></div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
                <div className="xl:col-span-2">
                    <Card className="bg-white/80">
                        {/* The collection notice is given at sign-up, with the full notice and the Privacy Policy
                            one click away; the health question carries its own consent line. Repeating the notice
                            here was removed on the owner's call (16 Oct 2026). */}
                        {step === 1 && <Step1_WhoIsCompleting factFind={factFind} setMainData={setMainData} />}
                        {step === 2 && (
                            <Step2_YourDetails
                                factFind={factFind}
                                updateFactFind={updateFactFind}
                                setMainData={setMainData}
                                isPartnered={isPartnered}
                                hideApplicantContactFields={hideApplicantContactFields}
                                voice={voice}
                            />
                        )}
                        {step === 3 && <Step3_GoalsCareNeeds factFind={factFind} updateFactFind={updateFactFind} isPartnered={isPartnered} voice={voice} />}
                        {step === 4 && (
                            <Step4_LivingSituation
                                factFind={factFind}
                                updateFactFind={updateFactFind}
                                isPartnered={isPartnered}
                                voice={voice}
                            />
                        )}
                        {step === 4 && residencyAnswered && ownsHome && (
                            <Step4_FamilyHome
                                home={factFind.home || {}}
                                updateFactFind={(f, v) => updateFactFind('home', f, v)}
                                client={factFind.client}
                                partner={factFind.partner}
                                isPartnered={isPartnered}
                                factFind={factFind}
                                voice={voice}
                                showHeader={false}
                                onHomeTitleOwnershipChange={(v) => {
                                    setFactFind((prev) => ({
                                        ...prev,
                                        structuresGateAnswer: v === 'other_entity' ? true : prev.structuresGateAnswer,
                                        home: {
                                            ...prev.home,
                                            ...(v !== 'other_entity' ? { entityId: '' } : {}),
                                        },
                                    }));
                                }}
                            />
                        )}
                        {step === 4 && residencyAnswered && hasAlternativeArrangement && (
                            <Step4_AltLiving factFind={factFind} updateFactFind={updateFactFind} voice={voice} showHeader={false} />
                        )}
                        {step === 5 && (
                            <>
                                <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                                    <button
                                        onClick={() => setShowAgePensionContext(!showAgePensionContext)}
                                        className="w-full p-4 text-left flex items-center justify-between hover:bg-blue-100 transition-colors duration-200"
                                    >
                                        <div className="flex items-center">
                                            <Info className="text-blue-600 mr-3 flex-shrink-0" size={20} />
                                            <h3 className="text-blue-800 font-semibold text-sm">Calculation Context</h3>
                                        </div>
                                        <ChevronDown
                                            className={`text-blue-600 transition-transform duration-200 ${showAgePensionContext ? 'rotate-180' : ''}`}
                                            size={16}
                                        />
                                    </button>
                                    {showAgePensionContext && (
                                        <div className="px-4 pb-4">
                                            <p className="text-blue-700 text-sm leading-relaxed">
                                                Our calculations automatically determine {voice.possessive} Age Pension eligibility based on {voice.pronounPossessive} age & the Social Security Asset Test and Income Test. As you enter {voice.pronounPossessive} financial details, you may find that the Age Pension amount shown is reduced. We also provide tax estimates. Please note: these calculations are for planning purposes only and do not constitute tax advice. Actual tax obligations may vary based on {voice.pronounPossessive} accountant's analysis, specific circumstances and current tax legislation. Complete all steps to see the impact on the financial snapshot.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <Step5_Assets
                                    factFind={factFind}
                                    setFactFind={setFactFind}
                                    isPartnered={isPartnered}
                                    showStructureAdviserFields={showStructureAdviserFields}
                                    voice={voice}
                                />
                            </>
                        )}
                        {step === 6 && (
                            <Step6_Liabilities
                                factFind={factFind}
                                setFactFind={setFactFind}
                                isPartnered={isPartnered}
                                voice={voice}
                            />
                        )}
                        {step === 7 && <Step7_IncomeExpenses factFind={factFind} updateFactFind={updateFactFind} isPartnered={isPartnered} voice={voice} />}
                        {step === 8 && <Step8_FamilyEstate factFind={factFind} updateFactFind={updateFactFind} isPartnered={isPartnered} voice={voice} />}

                        <div className="mt-8 pt-6 border-t border-slate-200 flex justify-between items-center gap-4">
                            <Button variant="secondary" size="md" icon={ChevronLeft} onClick={prevStep} disabled={step === 1}>Back</Button>
                            {step === 4 && !residencyAnswered && (
                                <p className="text-sm text-gray-500 text-right">
                                    Choose where {voice.subject} {voice.verb('live', 'lives')} to continue.
                                </p>
                            )}
                            <Button
                                variant="brand"
                                size="md"
                                iconRight={step === totalSteps ? undefined : ChevronRight}
                                disabled={step === 4 && !residencyAnswered}
                                onClick={step === totalSteps ? () => setActivePage('Dashboard') : nextStep}
                            >
                                {step === totalSteps ? 'Finish & View Dashboard' : 'Next'}
                            </Button>
                        </div>
                    </Card>
                </div>
                {isDesktop && (
                    <div className="sticky top-8">
                        {contextualInsight}
                    </div>
                )}
            </div>
            {/* Below `lg` the same content lives in a bottom sheet — see MobileInsightSheet. Steps
                with no insight column render no bar at all. */}
            {!isDesktop && contextualInsight && mobileInsightTitle && (
                <MobileInsightSheet
                    title={mobileInsightTitle}
                    value={snapshotNetWorth !== null && step >= 5 && step <= 7 ? formatCurrency(snapshotNetWorth) : null}
                >
                    {contextualInsight}
                </MobileInsightSheet>
            )}
            {showMentalCapacityModal && (
                <MentalCapacityProcessModal
                    onClose={() => setShowMentalCapacityModal(false)}
                />
            )}
        </div>
    );
};

export default ClientProfile;
